import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { adminAuth } from '@/api/admin';
import { useAuthStore } from '@/stores/auth';
import { LogoMark } from '@/layout/Logo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const setSession = useAuthStore((s) => s.setSession);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');

  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/dashboard';

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () =>
      adminAuth.login({ username, password, totpCode: totp.length > 0 ? totp : undefined }),
    onSuccess: (data) => {
      setErrorMsg(null);
      const name = username.trim();
      setSession(data.accessToken, {
        id: '0',
        username: name,
        displayName: name,
        role: 'admin',
      });
      navigate(fromPath, { replace: true });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '登录失败,请稍后重试';
      // 双保险:inline 红条 + sonner toast(toast 在某些环境下挂载有问题,inline 永远可见)
      setErrorMsg(msg);
      toast.error('登录失败', { description: msg });
    },
  });

  if (token) {
    return <Navigate to={fromPath} replace />;
  }

  const usernameOk = username.trim().length > 0;
  const passwordOk = password.length >= 4;
  // dev 期允许 TOTP 留空(后端 ADMIN_TOTP_ENABLED=false 时不校验);prod 必须 6 位数字
  const totpOk = import.meta.env.DEV ? totp.length === 0 || /^[0-9]{6}$/.test(totp) : /^[0-9]{6}$/.test(totp);
  const canSubmit = usernameOk && passwordOk && totpOk && !login.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-0 p-6">
      <Card className="w-full max-w-md elev-elev">
        <CardHeader className="space-y-3">
          <LogoMark />
          <div>
            <CardTitle className="text-lg">管理员登录</CardTitle>
            <CardDescription>需要管理员账号 + 6 位 TOTP 验证码 · 仅限白名单 IP 访问</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) login.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="login-username">账号</Label>
              <Input
                id="login-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={login.isPending}
                placeholder="admin"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-password">密码</Label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={login.isPending}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="login-totp">TOTP 验证码</Label>
              <Input
                id="login-totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={login.isPending}
                className="num text-center tracking-[0.4em]"
              />
            </div>

            {errorMsg && (
              <div className="rounded-md border border-down/40 bg-down/10 px-3 py-2 text-sm text-down">
                {errorMsg}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {login.isPending ? (
                '登录中...'
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  登录
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
